import {
  Article,
  Campaign,
  Email,
  Facebook,
  Instagram,
  Language,
  LinkedIn,
  MusicNote,
  Newspaper,
  Podcasts,
  X,
  YouTube,
} from "@mui/icons-material";

export interface ContentTypeConfig {
  name: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  subSettings?: Array<{
    label: string;
    name: string;
    type?: "checkbox" | "select";
    options?: Array<{ value: string; label: string }>;
  }>;
}

export const CONTENT_TYPES: ContentTypeConfig[] = [
  {
    name: "blog_post",
    title: "Blog Post",
    icon: <Article color="inherit" />,
    color: "#1976d2", // primary blue
    subSettings: [
      {
        label: "Auto-publish to blog",
        name: "autoPublish",
        type: "checkbox",
      },
      {
        label: "Publish as draft",
        name: "publishAsDraft",
        type: "checkbox",
      },
      {
        label: "Blog Integration",
        name: "wordpressIntegration",
        type: "select",
        options: [], // Will be populated dynamically from user's integrations
      },
    ],
  },
  {
    name: "facebook_post",
    title: "Facebook Post",
    icon: <Facebook color="inherit" />,
    color: "#1877f2",
    subSettings: [
      {
        label: "Auto-publish to Facebook",
        name: "autoPublish",
        type: "checkbox",
      },
      {
        label: "Facebook Integration",
        name: "facebookIntegration",
        type: "select",
        options: [], // Will be populated dynamically from user's integrations
      },
    ],
  },
  {
    name: "linkedin_post",
    title: "LinkedIn Post",
    icon: <LinkedIn color="inherit" />,
    color: "#0077b5",
    subSettings: [
      {
        label: "Auto-publish to LinkedIn",
        name: "autoPublish",
        type: "checkbox",
      },
      {
        label: "Publish as draft",
        name: "publishAsDraft",
        type: "checkbox",
      },
      {
        label: "LinkedIn Integration",
        name: "linkedinIntegration",
        type: "select",
        options: [], // Will be populated dynamically from user's integrations
      },
    ],
  },
  {
    name: "instagram_post",
    title: "Instagram Post",
    icon: <Instagram color="inherit" />,
    color:
      "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    subSettings: [
      {
        label: "Auto-publish to Instagram",
        name: "autoPublish",
        type: "checkbox",
      },
      {
        label: "Instagram Integration",
        name: "instagramIntegration",
        type: "select",
        options: [], // Will be populated dynamically from user's integrations
      },
    ],
  },
  {
    name: "x_post",
    title: "X Post",
    icon: <X color="inherit" />, // No icon for X
    color: "#000000",
  },
  {
    name: "youtube_video",
    title: "YouTube Video",
    icon: <YouTube color="inherit" />,
    color: "#ff0000",
  },
  {
    name: "tiktok_video",
    title: "TikTok Video",
    icon: <MusicNote color="inherit" />,
    color: "#000000",
  },
  {
    name: "email_newsletter",
    title: "Email Newsletter",
    icon: <Email color="inherit" />,
    color: "#34a853",
  },
  {
    name: "podcast_episode",
    title: "Podcast Episode",
    icon: <Podcasts color="inherit" />,
    color: "#9c27b0",
  },
  {
    name: "website_copy",
    title: "Website Copy",
    icon: <Language color="inherit" />,
    color: "#607d8b",
  },
  {
    name: "marketing_email",
    title: "Marketing Email",
    icon: <Campaign color="inherit" />,
    color: "#ff5722",
    subSettings: [
      {
        label: "Auto-publish as marketing campaign draft",
        name: "autoPublish",
        type: "checkbox",
      },
      {
        label: "Marketing Campaign Template",
        name: "marketingCampaignTemplate",
        type: "select",
        options: [], // Will be populated dynamically from user's templates
      },
    ],
  },
  {
    name: "ad_copy",
    title: "Ad Copy",
    icon: <Campaign color="inherit" />,
    color: "#e91e63",
  },
  {
    name: "press_release",
    title: "Press Release",
    icon: <Newspaper color="inherit" />,
    color: "#455a64", // blue-grey for professional/media feel
  },
];

// Helper function to get content type config by name
export const getContentTypeConfig = (
  typeName: string
): ContentTypeConfig | undefined => {
  return CONTENT_TYPES.find((type) => type.name === typeName);
};

// Content type names only (for dropdown options, etc.)
export const CONTENT_TYPE_OPTIONS = CONTENT_TYPES.map((type) => ({
  value: type.name,
  label: type.title,
}));

// For default campaign structure
export const DEFAULT_CHANNELS_CONFIG = CONTENT_TYPES.reduce((acc, type) => {
  const config: any = { enabled: false };

  // Add sub-settings if they exist
  if (type.subSettings && type.subSettings.length > 0) {
    config.subSettings = {};
    type.subSettings.forEach((setting) => {
      if (setting.type === "checkbox") {
        config.subSettings[setting.name] = false;
      } else if (setting.type === "select") {
        config.subSettings[setting.name] = "";
      }
    });
  }

  acc[type.name] = config;
  return acc;
}, {} as Record<string, any>);
